import React from 'react';
import { formatMoney } from '../../utils/format';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { focusRing } from '../../theme/tokens';

/**
 * CartAwarePriceBadge — visually distinguishes cart-applied / on-sale competitor prices.
 *
 * @description
 *   Renders a small "Cart -N%" chip + strikethrough sticker when the
 *   competitor's checkout price is below their visible PDP price. Source can
 *   be ShopifyCartFetcher (Phase 3 cart auto-discount) OR HtmlFetcher's
 *   strikethrough — same UX either way.
 *   Returns just the bare price when no discount is observable.
 *
 * @useWhen
 *   - Displaying competitor prices captured by any sale-aware fetcher
 *   - Anywhere comp_price + comp_sticker_price are both available in scope
 *   - Scraper Run-Now test result, Product Detail Market Pricing card, Vendor Reports median row
 *
 * @dontUseFor
 *   - Generic price formatting — use existing currency/format utils
 *   - Our own (Brewout) prices — different concept
 *   - When competitor is out-of-stock — pass available={false} to hide badge
 *   - Historical price-change-log rows (price_change_log table doesn't carry
 *     sticker yet — see docs/ideas/2026-05-15-pricing-changelog-sticker.md)
 *
 * @related
 *   - MUI Chip + Tooltip — base primitives
 *
 * @reuseHints
 *   - Pricing Automation dashboard (once price_change_log carries sticker)
 *   - Market Lab signal explanations
 *   - Future PriceCell expansion
 *
 * @props
 *   price (number, required) — cart-applied / sale price
 *   stickerPrice (number|null) — pre-discount PDP sticker; renders bare when missing/equal
 *   currency (string, optional) — currency code for formatting; defaults to EUR
 *   available (bool, optional) — when false, render bare price even with sticker (OOS protection)
 *   size (small|medium) — chip + text size; defaults to small
 */
const CartAwarePriceBadge = ({
  price,
  stickerPrice = null,
  currency = 'EUR',
  available = true,
  size = 'small',
}) => {
  const fmt = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(n));
    } catch {
      return formatMoney(n, { currency });
    }
  };

  // No price at all → render dash
  if (price === null || price === undefined) {
    return <Typography variant={size === 'small' ? 'body2' : 'body1'}>—</Typography>;
  }

  // Gate: bare display when no observable discount OR competitor OOS.
  const hasDiscount =
    stickerPrice !== null &&
    stickerPrice !== undefined &&
    Number(stickerPrice) > Number(price) + 0.001; // tiny epsilon for float noise

  if (!hasDiscount || !available) {
    return (
      <Typography variant={size === 'small' ? 'body2' : 'body1'} component="span">
        {fmt(price)}
      </Typography>
    );
  }

  const discountAmount = Number(stickerPrice) - Number(price);
  const discountPct = Math.round((discountAmount / Number(stickerPrice)) * 100);

  // Microcopy stays operator-facing — no implementation/class names leak.
  const tooltip = `Sticker ${fmt(stickerPrice)}, checkout ${fmt(price)} — ${fmt(discountAmount)} off (${discountPct}%). Captured from the competitor's cart or product page.`;

  return (
    <Tooltip title={tooltip} arrow>
      {/* tabIndex + role so keyboard users can focus the badge and the Tooltip
          opens via focus event (MUI handles focus / blur natively when the
          child is focusable). Span with role="text" stays inline-friendly. */}
      <Box
        component="span"
        tabIndex={0}
        role="text"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          // Subtle focus ring for keyboard users; falls back to MUI default outline
          // The badge is an INFO-coloured surface, so its ring follows `info`.
          '&:focus-visible': { ...focusRing({ farbe: 'info.main' }), borderRadius: 0.5 },
        }}
        aria-label={`Discounted price ${fmt(price)}, sticker ${fmt(stickerPrice)}`}
      >
        <Typography
          variant={size === 'small' ? 'body2' : 'body1'}
          component="span"
          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
        >
          {fmt(stickerPrice)}
        </Typography>
        <Typography
          variant={size === 'small' ? 'body2' : 'body1'}
          component="span"
          sx={{ fontWeight: 600 }}
        >
          {fmt(price)}
        </Typography>
        <Chip
          size={size}
          label={`-${discountPct}%`}
          color="info"
          sx={{ height: size === 'small' ? 18 : 22, fontSize: size === 'small' ? '0.7rem' : '0.75rem' }}
        />
      </Box>
    </Tooltip>
  );
};

export default CartAwarePriceBadge;
